import { lazy } from "react";

/**
 * lazyWithRetry — wraps React.lazy() so that a ChunkLoadError caused by a
 * stale cached bundle automatically force-reloads the page with a cache-busting
 * query string.
 *
 * Updated retry strategy (more aggressive — handles slow CDN propagation):
 *   - Up to 3 cache-busting reloads per session (was 1).
 *   - Each retry waits ~600ms longer than the last (lets CDN catch up).
 *   - After the 3rd failed retry, redirect to "/" instead of showing a broken
 *     page. Going home is always safe and gets the user back into a working
 *     state.
 *   - The retry counter is cleared on successful page mount in App.js so
 *     subsequent deploys are caught fresh.
 */
const RETRY_COUNT_KEY = "jr_chunk_retry_count";
const MAX_RETRIES = 3;

function isChunkLoadError(err) {
  if (!err) return false;
  const msg = (err && (err.message || err.toString())) || "";
  return (
    err.name === "ChunkLoadError" ||
    /Loading chunk [\d]+ failed/i.test(msg) ||
    /Loading CSS chunk [\d]+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg)
  );
}

function getRetryCount() {
  try { return parseInt(sessionStorage.getItem(RETRY_COUNT_KEY) || "0", 10) || 0; } catch (e) { void e; return 0; }
}
function setRetryCount(n) {
  try { sessionStorage.setItem(RETRY_COUNT_KEY, String(n)); } catch (e) { void e; }
}

function purgeCachesAndSW() {
  try {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations().then((regs) => {
        regs.forEach((r) => r.unregister().catch(() => {}));
      }).catch(() => {});
    }
    if (window.caches && window.caches.keys) {
      window.caches.keys().then((keys) => {
        keys.forEach((k) => window.caches.delete(k).catch(() => {}));
      }).catch(() => {});
    }
  } catch (e) { void e; }
}

function hardReloadWithCacheBust(delayMs = 0) {
  purgeCachesAndSW();
  const sep = window.location.search.includes("?") ? "&" : "?";
  const url = window.location.pathname + window.location.search + sep + "_v=" + Date.now();
  if (delayMs > 0) {
    setTimeout(() => window.location.replace(url), delayMs);
  } else {
    window.location.replace(url);
  }
}

function safelyGoHome() {
  // Fallback after MAX_RETRIES — bounce to "/" with cache-bust so user always
  // ends up somewhere working, not a black/broken screen.
  purgeCachesAndSW();
  const url = "/?_v=" + Date.now() + "&recovered=1";
  window.location.replace(url);
}

export function lazyWithRetry(factory) {
  return lazy(() =>
    factory().catch((err) => {
      if (!isChunkLoadError(err)) throw err;
      const count = getRetryCount();
      if (count >= MAX_RETRIES) {
        // Bail to home (safe) instead of throwing into the boundary UI.
        safelyGoHome();
        return new Promise(() => {});
      }
      setRetryCount(count + 1);
      // Progressive backoff: 0ms, 800ms, 1600ms — gives CDN time to propagate.
      hardReloadWithCacheBust(count * 800);
      return new Promise(() => {});
    })
  );
}

// Global handler — catches chunk-load promise rejections that escape
// lazyWithRetry (e.g. dynamic imports inside route components).
let _installed = false;
export function installGlobalChunkErrorReload() {
  if (_installed || typeof window === "undefined") return;
  _installed = true;
  window.addEventListener("unhandledrejection", (event) => {
    if (!isChunkLoadError(event.reason)) return;
    const count = getRetryCount();
    if (count >= MAX_RETRIES) {
      event.preventDefault?.();
      safelyGoHome();
      return;
    }
    setRetryCount(count + 1);
    event.preventDefault?.();
    hardReloadWithCacheBust(count * 800);
  });
  // Catch sync errors from <script src="...chunk.js"> tags.
  window.addEventListener("error", (event) => {
    const t = event?.target;
    if (!t || (t.tagName !== "SCRIPT" && t.tagName !== "LINK")) return;
    const src = t.src || t.href || "";
    if (!/\.(chunk\.)?(js|css)(\?|$)/i.test(src)) return;
    if (!/\d+\.[a-f0-9]+\.chunk\.(js|css)/i.test(src)) return;
    const count = getRetryCount();
    if (count >= MAX_RETRIES) {
      safelyGoHome();
      return;
    }
    setRetryCount(count + 1);
    hardReloadWithCacheBust(count * 800);
  }, true);
}

// Call this once the app mounts so a fresh deploy can be caught again next time.
export function clearChunkReloadFlag() {
  try { sessionStorage.removeItem(RETRY_COUNT_KEY); } catch (e) { void e; }
  // Also remove the legacy single-shot flag from older code.
  try { sessionStorage.removeItem("jr_chunk_reload_attempted"); } catch (e) { void e; }
}
